import React, { useState, useEffect, useMemo, useRef } from "react";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
    cargarSoportes,
    agregarSoporte,
    modificarSoporte,
    borrarSoporte
} from "../controllers/soporteControllers";
import { eliminarCasoExternoSoporte, obtenerCasosIsmael, obtenerEquipos, obtenerFallasDispositivos, resolverPlantillaDiagrama, enviarAvisoSoporteTerreno } from "../api";
import SoporteAvisoTerreno, { EstadoAvisoTerreno, ResultadoAvisoTerreno } from "../components/SoporteAvisoTerreno";
import { admiteAvisoTerreno, notificarSoporteGuardado } from "../utils/soporteAviso";
import { cargarCentrosClientes } from "../controllers/centrosControllers";
import DiagramaLogicoViewer from "../components/DiagramaLogicoViewer";
import VistaGeneralViewer from "../components/VistaGeneralViewer";
import "./Soporte.css";

const CATEGORIAS_FALLA = [
    "ENERGIA",
    "CAMARAS",
    "RADAR",
    "SENSOR",
    "SOFTWARE",
    "HARDWARE",
    "CONFIGURACION",
    "MANTENCION",
    "ENLACE",
    "ALARMAS",
    "LICENCIA",
    "USUARIO",
    "NETIO",
    "REDES"
];

const SUBCATEGORIAS_FALLA = {
    ENERGIA: ["VICTRON VRM", "CARGADOR", "BATERIAS", "TERCEROS", "UPS", "OTRA"],
    CAMARAS: ["CAMARA LASER", "CAMARA TERMAL", "CAMARA INTERIOR", "CAMARA SILO", "CAMARA ENSILAJE", "CAMARA ACCESO", "CAMARA MODULO", "OTRA"],
    RADAR: ["PANEL RADAR", "PLATAFORMA WEB", "CABLE RADAR", "CALIBRACION", "OTRA"],
    SOFTWARE: ["DIGIFORT", "MAGOS", "PLANILLA", "METRICA DE ENERGIA", "WINDOWS", "OTRA"],
    HARDWARE: ["PC", "NUC", "OTRA"],
    CONFIGURACION: ["PRESET", "ALARMAS AXIS", "CAMARA", "PC", "OTRA"],
    ALARMAS: ["FUENTE PODER", "AXIS", "FOCO LED", "BALIZA EXTERIOR", "BALIZA INTERIOR", "BOCINA EXTERIOR", "BOCINA INTERIOR", "OTRA"],
    LICENCIA: ["RADAR", "DIGIFORT", "OTRA"],
    REDES: ["IP STATIC", "IP DHCP", "PROBLEMA DE CABLEADO", "ROUTER", "SWITCH", "OTRA"]
};

const MAPEO_CATEGORIAS_LEGACY = {
    UPS: { categoria: "ENERGIA", subcategoria: "UPS" },
    VRM: { categoria: "ENERGIA", subcategoria: "VICTRON VRM" },
    BATERIAS: { categoria: "ENERGIA", subcategoria: "BATERIAS" },
    GENERADOR: { categoria: "ENERGIA", subcategoria: "OTRA" },
    TERCEROS: { categoria: "ENERGIA", subcategoria: "TERCEROS" },
    CALIBRACION: { categoria: "RADAR", subcategoria: "CALIBRACION" },
    ROUTER: { categoria: "REDES", subcategoria: "ROUTER" },
    SWITCH: { categoria: "REDES", subcategoria: "SWITCH" },
    PC: { categoria: "HARDWARE", subcategoria: "PC" }
};

const normalizarTextoCategoria = (valor) =>
    String(valor || "")
        .trim()
        .toUpperCase();

const obtenerSubcategoriasPorCategoria = (categoria) => {
    const categoriaNormalizada = normalizarTextoCategoria(categoria);
    return SUBCATEGORIAS_FALLA[categoriaNormalizada] || [];
};

const resolverCategoriaExistente = (categoria, subcategoria) => {
    const categoriaNormalizada = normalizarTextoCategoria(categoria);
    const subcategoriaNormalizada = normalizarTextoCategoria(subcategoria);

    if (CATEGORIAS_FALLA.includes(categoriaNormalizada)) {
        const subcategoriasDisponibles = obtenerSubcategoriasPorCategoria(categoriaNormalizada);
        return {
            categoria: categoriaNormalizada,
            subcategoria: subcategoriasDisponibles.includes(subcategoriaNormalizada) ? subcategoriaNormalizada : ""
        };
    }

    if (MAPEO_CATEGORIAS_LEGACY[categoriaNormalizada]) {
        return MAPEO_CATEGORIAS_LEGACY[categoriaNormalizada];
    }

    return { categoria: "", subcategoria: "" };
};

const obtenerTotalUnico = (items, accessor) => {
    const valores = items
        .map(accessor)
        .filter((valor) => !!valor);
    return new Set(valores).size;
};

const parseFechaLocal = (valor, finDeDia = false) => {
    if (!valor) return null;

    const texto = String(valor).trim();
    // Prioriza la parte YYYY-MM-DD incluso si viene con hora/UTC.
    const soloFecha = /^(\d{4})-(\d{2})-(\d{2})/.exec(texto);
    let fecha = null;

    if (soloFecha) {
        const anio = Number(soloFecha[1]);
        const mes = Number(soloFecha[2]) - 1;
        const dia = Number(soloFecha[3]);
        fecha = new Date(anio, mes, dia);
    } else {
        fecha = new Date(texto);
        if (Number.isNaN(fecha.getTime())) return null;
    }

    if (finDeDia) {
        fecha.setHours(23, 59, 59, 999);
    } else {
        fecha.setHours(0, 0, 0, 0);
    }

    return fecha;
};

const formatearEtiquetaCentro = (centro) => {
    if (!centro) return "";
    const estado = String(centro.estado || "").trim();
    return `${centro.id} - ${centro.nombre}${centro.cliente ? ` (${centro.cliente})` : ""}${estado ? ` · ${estado}` : ""}`;
};

const normalizarEstadoCentro = (estado) =>
    String(estado || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, "-");

const normalizarCoincidencia = (valor) => String(valor || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const resolverCentroAlerta = (alerta, centros) => {
    const lista = Array.isArray(centros) ? centros : [];
    const idSite = String(alerta?.id_site || "").trim();
    if (idSite) {
        const porId = lista.find((centro) => String(centro.id) === idSite);
        if (porId) return porId;
    }
    const nombreCentro = normalizarCoincidencia(alerta?.centro || alerta?.router_id);
    const nombreCliente = normalizarCoincidencia(alerta?.cliente);
    return lista.find((centro) => (
        normalizarCoincidencia(centro.nombre) === nombreCentro
        && (!nombreCliente || normalizarCoincidencia(centro.cliente) === nombreCliente)
    )) || lista.find((centro) => normalizarCoincidencia(centro.nombre) === nombreCentro) || null;
};

const normalizarListaDetalles = (cantidad, detalles = []) => {
    const cant = Number(cantidad || 0);
    if (!cant || cant < 1) return [];
    const listaBase = Array.isArray(detalles) ? detalles : [];
    return Array.from({ length: cant }, (_, index) =>
        String(listaBase[index] || "").trim().toUpperCase()
    );
};

const construirDetalleCambioEquipo = (cantidad, detalles = []) => {
    const cant = Number(cantidad || 0);
    const lista = normalizarListaDetalles(cant, detalles);
    if (!cant || !lista.length) return "";
    return `TOTAL:${cant} | ITEMS:${lista.join(" || ")}`;
};

const parsearDetalleCambioEquipo = (texto) => {
    const raw = String(texto || "").trim();
    const matchItems = /^TOTAL:\s*(\d+)\s*\|\s*ITEMS:\s*(.+)$/i.exec(raw);
    if (matchItems) {
        const cantidad = Number(matchItems[1] || 0);
        const detalles = String(matchItems[2] || "")
            .split("||")
            .map((item) => String(item || "").trim().toUpperCase());
        return {
            cantidad,
            detalles: normalizarListaDetalles(cantidad, detalles)
        };
    }

    const match = /^TOTAL:\s*(\d+)\s*\|\s*DETALLE:\s*(.+)$/i.exec(raw);
    if (match) {
        const cantidad = Number(match[1] || 0);
        const detalleTexto = String(match[2] || "").trim().toUpperCase();
        return {
            cantidad,
            detalles: normalizarListaDetalles(
                cantidad,
                cantidad > 1 ? Array.from({ length: cantidad }, () => detalleTexto) : [detalleTexto]
            )
        };
    }
    const detallesFallback = raw ? [raw.toUpperCase()] : [];
    return { cantidad: detallesFallback.length ? 1 : "", detalles: detallesFallback };
};

const obtenerCantidadCambioSoporte = (soporte) => {
    if (!soporte?.cambio_equipo) return 0;
    const parsed = parsearDetalleCambioEquipo(soporte?.equipo_cambiado);
    const cantidad = Number(parsed?.cantidad || 0);
    return cantidad > 0 ? cantidad : 1;
};

const obtenerNombresCambioSoporte = (soporte) => {
    if (!soporte?.cambio_equipo) return "-";
    const parsed = parsearDetalleCambioEquipo(soporte?.equipo_cambiado);
    if (parsed?.detalles?.length) return parsed.detalles.join(", ");
    return String(soporte?.equipo_cambiado || "-");
};

const obtenerEtiquetaTipoSoporte = (tipo) => {
    const valor = String(tipo || "").trim().toLowerCase();
    if (valor === "remoto") return "Remoto";
    if (valor === "terreno") return "Terreno";
    return valor || "-";
};

const obtenerClaseTipoSoporte = (tipo) => {
    const valor = String(tipo || "").trim().toLowerCase();
    if (valor === "remoto") return "urgent-type-chip-remote";
    return "urgent-type-chip-terrain";
};

const Soporte = () => {
    const tableScrollRef = useRef(null);
    const dragScrollStateRef = useRef({
        isDown: false,
        isDragging: false,
        startX: 0,
        scrollLeft: 0,
        pointerId: null,
        scrollTarget: null,
        suppressClick: false
    });
    const [soportes, setSoportes] = useState([]);
    const [centros, setCentros] = useState([]);
    const [loading, setLoading] = useState(true);
    const [casosIsmael, setCasosIsmael] = useState([]);
    const [fallasDispositivos, setFallasDispositivos] = useState([]);
    const navigate = useNavigate();

    // Estados del formulario
    const [centroId, setCentroId] = useState("");
    const [centroBusqueda, setCentroBusqueda] = useState("");
    const [mostrarSugerenciasCentro, setMostrarSugerenciasCentro] = useState(false);
    const [problema, setProblema] = useState("");
    const [tipo, setTipo] = useState("");
    const [origen, setOrigen] = useState("cliente");
    const [prioridadSoporte, setPrioridadSoporte] = useState("media");
    const [fechaSoporte, setFechaSoporte] = useState("");
    const [solucion, setSolucion] = useState("");
    const [sugerencia, setSugerencia] = useState("");
    const [enviarAvisoTerreno, setEnviarAvisoTerreno] = useState(false);
    const [resultadoAviso, setResultadoAviso] = useState(null);
    const [reintentandoAviso, setReintentandoAviso] = useState(false);
    const [categoriaFalla, setCategoriaFalla] = useState("");
    const [subcategoriaFalla, setSubcategoriaFalla] = useState("");
    const [permisoTrabajo, setPermisoTrabajo] = useState(false);
    const [cambioEquipo, setCambioEquipo] = useState(false);
    const [cantidadEquiposCambiados, setCantidadEquiposCambiados] = useState("");
    const [detalleEquiposCambiadosLista, setDetalleEquiposCambiadosLista] = useState([]);
    const [estado, setEstado] = useState("pendiente");
    const [caseCode, setCaseCode] = useState("");
    const [ismaelIdOrigen, setIsmaelIdOrigen] = useState("");
    const [externalCaseKey, setExternalCaseKey] = useState("");
    const [fechaCierre, setFechaCierre] = useState("");
    const [errorFechaCierre, setErrorFechaCierre] = useState("");
    const [editarSoporte, setEditarSoporte] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [guardandoSoporte, setGuardandoSoporte] = useState(false);
    useEffect(() => {
        if (!admiteAvisoTerreno(tipo, estado, fechaCierre)) setEnviarAvisoTerreno(false);
    }, [tipo, estado, fechaCierre]);
    const [diagramaConsulta, setDiagramaConsulta] = useState({
        abierto: false,
        cargando: false,
        error: "",
        plantilla: null,
        origen: null,
        soporte: null
    });

    // Estado para filtrar por periodo
    const [filtroPeriodo, setFiltroPeriodo] = useState("anio-actual");
    const [fechaInicioPersonalizada, setFechaInicioPersonalizada] = useState("");
    const [fechaFinPersonalizada, setFechaFinPersonalizada] = useState("");
    const [filtroNombre, setFiltroNombre] = useState("");
    const [fechaInicioBusqueda, setFechaInicioBusqueda] = useState("");
    const [fechaFinBusqueda, setFechaFinBusqueda] = useState("");
    const [mostrarCambiosEquipo, setMostrarCambiosEquipo] = useState(false);
    const [ismaelSeleccionado, setIsmaelSeleccionado] = useState(null);
    const [topologiaFalla, setTopologiaFalla] = useState({ cargando: false, plantilla: null, falla: null, origen: null, error: "" });
    const topologiaRequestRef = useRef(0);
    const [mostrarCorreoIsmael, setMostrarCorreoIsmael] = useState(false);
    const [eliminandoCasoExterno, setEliminandoCasoExterno] = useState("");
    const [paginaIsmael, setPaginaIsmael] = useState(1);
    const [paginaPendientes, setPaginaPendientes] = useState(1);
    const registrosPorTarjeta = 4;
    const esAdmin = useMemo(() => {
        try {
            const token = localStorage.getItem("token");
            return token && String(jwtDecode(token)?.rol || "").trim().toLowerCase() === "admin";
        } catch {
            return false;
        }
    }, []);

    const refrescarSoportes = async () => {
        setLoading(true);
        await cargarSoportes(setSoportes);
        try {
            const [casos, fallas] = await Promise.all([
                obtenerCasosIsmael({ limit: 40 }),
                obtenerFallasDispositivos({ limit: 200 })
            ]);
            setCasosIsmael(Array.isArray(casos) ? casos : []);
            setFallasDispositivos(Array.isArray(fallas) ? fallas : []);
        } catch {
            setCasosIsmael([]);
            setFallasDispositivos([]);
        }
        setLoading(false);
    };

    // Cargar datos
    useEffect(() => {
        const fetchData = async () => {
            await refrescarSoportes();
            const centrosData = await cargarCentrosClientes();
            setCentros(centrosData);
        };
        fetchData();
    }, []);

    useEffect(() => {
        const intervalo = window.setInterval(async () => {
            try {
                const [casos, fallas] = await Promise.all([
                    obtenerCasosIsmael({ limit: 40 }),
                    obtenerFallasDispositivos({ limit: 200 })
                ]);
                setCasosIsmael(Array.isArray(casos) ? casos : []);
                setFallasDispositivos(Array.isArray(fallas) ? fallas : []);
            } catch (error) {
                console.error("No se pudieron actualizar los casos externos:", error);
            }
        }, 15000);
        return () => window.clearInterval(intervalo);
    }, []);

    useEffect(() => {
        if (!centroId || !centros.length) return;
        const match = centros.find((centro) => String(centro.id) === String(centroId));
        if (match) {
            setCentroBusqueda(formatearEtiquetaCentro(match));
        }
    }, [centroId, centros]);

    useEffect(() => {
        setMostrarCorreoIsmael(false);
    }, [ismaelSeleccionado]);

    const centroSeleccionado = useMemo(
        () => centros.find((centro) => String(centro.id) === String(centroId)) || null,
        [centros, centroId]
    );
    const claseEstadoCentroInput = centroSeleccionado
        ? `centro-input-estado-${normalizarEstadoCentro(centroSeleccionado.estado) || "sin-estado"}`
        : "";
    const subcategoriasDisponibles = useMemo(
        () => obtenerSubcategoriasPorCategoria(categoriaFalla),
        [categoriaFalla]
    );

    const centrosSugeridos = useMemo(() => {
        const texto = String(centroBusqueda || "").trim().toLowerCase();
        if (!texto) return centros.slice(0, 8);
        return centros
            .filter((centro) => {
                const etiqueta = formatearEtiquetaCentro(centro).toLowerCase();
                return etiqueta.includes(texto);
            })
            .slice(0, 8);
    }, [centros, centroBusqueda]);

    useEffect(() => {
        if (!categoriaFalla) {
            setSubcategoriaFalla("");
            return;
        }
        if (subcategoriasDisponibles.length && !subcategoriasDisponibles.includes(subcategoriaFalla)) {
            setSubcategoriaFalla("");
        }
        if (!subcategoriasDisponibles.length && subcategoriaFalla) {
            setSubcategoriaFalla("");
        }
    }, [categoriaFalla, subcategoriaFalla, subcategoriasDisponibles]);

    const rangoFechas = useMemo(() => {
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);

        const construirFecha = (valor, finDeDia = false) => {
            return parseFechaLocal(valor, finDeDia);
        };

        switch (filtroPeriodo) {
            case "mes-actual": {
                const inicio = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
                const fin = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0, 23, 59, 59, 999);
                return { inicio, fin };
            }
            case "mes-anterior": {
                const inicio = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
                const fin = new Date(hoy.getFullYear(), hoy.getMonth(), 0, 23, 59, 59, 999);
                return { inicio, fin };
            }
            case "anio-actual": {
                const inicio = new Date(hoy.getFullYear(), 0, 1);
                const fin = new Date(hoy.getFullYear(), 11, 31, 23, 59, 59, 999);
                return { inicio, fin };
            }
            case "anio-anterior": {
                const inicio = new Date(hoy.getFullYear() - 1, 0, 1);
                const fin = new Date(hoy.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
                return { inicio, fin };
            }
            case "personalizado":
                return {
                    inicio: construirFecha(fechaInicioPersonalizada),
                    fin: construirFecha(fechaFinPersonalizada, true)
                };
            default:
                return { inicio: null, fin: null };
        }
    }, [filtroPeriodo, fechaInicioPersonalizada, fechaFinPersonalizada]);

    const descripcionPeriodo = useMemo(() => {
        switch (filtroPeriodo) {
            case "mes-actual":
                return "Mes actual";
            case "mes-anterior":
                return "Mes anterior";
            case "anio-actual":
                return "Año actual";
            case "anio-anterior":
                return "Año anterior";
            case "personalizado": {
                if (fechaInicioPersonalizada || fechaFinPersonalizada) {
                    return `Personalizado (${fechaInicioPersonalizada || "sin inicio"} - ${fechaFinPersonalizada || "sin fin"})`;
                }
                return "Personalizado";
            }
            default:
                return "General";
        }
    }, [filtroPeriodo, fechaInicioPersonalizada, fechaFinPersonalizada]);

    // Filtrar soportes por periodo seleccionado
    const soportesFiltrados = useMemo(() => {
        return soportes.filter((soporte) => {
            if (!soporte.fecha_soporte) return false;
            const fecha = parseFechaLocal(soporte.fecha_soporte);
            if (!fecha || Number.isNaN(fecha.getTime())) {
                return false;
            }
            if (rangoFechas.inicio && fecha < rangoFechas.inicio) {
                return false;
            }
            if (rangoFechas.fin && fecha > rangoFechas.fin) {
                return false;
            }
            return true;
        });
    }, [soportes, rangoFechas]);

    const soportesFiltradosBusqueda = useMemo(() => {
        const normalizarTexto = (valor = "") =>
            String(valor)
                .toLowerCase()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "");

        const nombreBusqueda = normalizarTexto(filtroNombre).trim();

        const inicioBusqueda = parseFechaLocal(fechaInicioBusqueda);
        const finBusqueda = parseFechaLocal(fechaFinBusqueda, true);

        return soportesFiltrados.filter((soporte) => {
            const fecha = soporte.fecha_soporte ? parseFechaLocal(soporte.fecha_soporte) : null;
            if (!fecha || Number.isNaN(fecha.getTime())) return false;

            if (inicioBusqueda && fecha < inicioBusqueda) return false;
            if (finBusqueda && fecha > finBusqueda) return false;

            if (!nombreBusqueda) return true;

            const candidato = normalizarTexto(
                [
                    soporte.centro?.nombre,
                    soporte.centro?.cliente,
                    soporte.problema,
                    soporte.case_code,
                    soporte.categoria_falla,
                    soporte.subcategoria_falla,
                    soporte.tipo,
                    soporte.solucion,
                    soporte.sugerencia,
                    esAdmin ? soporte.creado_por_nombre : null
                ]
                    .filter(Boolean)
                    .join(" ")
            );
            return candidato.includes(nombreBusqueda);
        });
    }, [soportesFiltrados, filtroNombre, fechaInicioBusqueda, fechaFinBusqueda, esAdmin]);

    // Métricas
    const totalSoportesGeneral = soportes.length;
    const totalSoportesPeriodo = soportesFiltrados.length;
    const totalCambiosEquiposPeriodo = useMemo(
        () =>
            soportesFiltrados.reduce(
                (acc, soporte) => acc + obtenerCantidadCambioSoporte(soporte),
                0
            ),
        [soportesFiltrados]
    );
    const totalCambiosEquiposGeneral = useMemo(
        () =>
            soportes.reduce(
                (acc, soporte) => acc + obtenerCantidadCambioSoporte(soporte),
                0
            ),
        [soportes]
    );
    const totalClientesGeneral = useMemo(
        () => obtenerTotalUnico(soportes, (soporte) => soporte.centro?.cliente),
        [soportes]
    );
    const totalClientesPeriodo = useMemo(
        () => obtenerTotalUnico(soportesFiltrados, (soporte) => soporte.centro?.cliente),
        [soportesFiltrados]
    );
    const totalCentrosGeneral = useMemo(
        () => obtenerTotalUnico(soportes, (soporte) => soporte.centro?.nombre),
        [soportes]
    );
    const totalCentrosPeriodo = useMemo(
        () => obtenerTotalUnico(soportesFiltrados, (soporte) => soporte.centro?.nombre),
        [soportesFiltrados]
    );

    const resetForm = () => {
        setCentroId("");
        setCentroBusqueda("");
        setProblema("");
        setTipo("");
        setOrigen("cliente");
        setPrioridadSoporte("media");
        setFechaSoporte("");
        setSolucion("");
        setSugerencia("");
        setEnviarAvisoTerreno(false);
        setCategoriaFalla("");
        setSubcategoriaFalla("");
        setPermisoTrabajo(false);
        setCambioEquipo(false);
        setCantidadEquiposCambiados("");
        setDetalleEquiposCambiadosLista([]);
        setEditarSoporte(null);
        setEstado("pendiente");
        setCaseCode("");
        setIsmaelIdOrigen("");
        setExternalCaseKey("");
        setFechaCierre("");
        setErrorFechaCierre("");
    };

    const handleCentroBusquedaChange = (valor) => {
        setCentroBusqueda(valor);
        if (!valor) {
            setCentroId("");
            return;
        }
        const posibleId = valor.split(" - ")[0]?.trim();
        const match = centros.find((centro) => String(centro.id) === posibleId);
        if (match) {
            setCentroId(String(match.id));
            setCentroBusqueda(formatearEtiquetaCentro(match));
        } else {
            setCentroId("");
        }
    };

    const handleSeleccionarCentroSugerido = (centro) => {
        setCentroId(String(centro.id));
        setCentroBusqueda(formatearEtiquetaCentro(centro));
        setMostrarSugerenciasCentro(false);
    };

    const cerrarDetalleCaso = () => {
        topologiaRequestRef.current += 1;
        setIsmaelSeleccionado(null);
        setMostrarCorreoIsmael(false);
        setTopologiaFalla({ cargando: false, plantilla: null, falla: null, origen: null, error: "" });
    };

    const abrirDetalleCaso = async (row) => {
        setIsmaelSeleccionado(row);
        setMostrarCorreoIsmael(false);
        if (row?.tipo_fuente !== "dispositivo") {
            setTopologiaFalla({ cargando: false, plantilla: null, falla: null, origen: null, error: "" });
            return;
        }

        const solicitudId = topologiaRequestRef.current + 1;
        topologiaRequestRef.current = solicitudId;
        setTopologiaFalla({ cargando: true, plantilla: null, falla: row, origen: null, error: "" });
        const centroAlerta = resolverCentroAlerta(row, centros);
        const centroCliente = centroAlerta || (Array.isArray(centros) ? centros : []).find(
            (centro) => normalizarCoincidencia(centro.cliente) === normalizarCoincidencia(row?.cliente)
        );

        if (!centroAlerta && !centroCliente?.cliente_id) {
            setTopologiaFalla({
                cargando: false,
                plantilla: null,
                falla: row,
                origen: null,
                error: "No fue posible identificar el centro o cliente de esta alerta."
            });
            return;
        }

        try {
            const [respuestaDiagrama, equipos] = await Promise.all([
                resolverPlantillaDiagrama({
                    centroId: centroAlerta?.id,
                    clienteId: centroAlerta ? undefined : centroCliente?.cliente_id
                }),
                centroAlerta?.id ? obtenerEquipos(centroAlerta.id).catch(() => []) : Promise.resolve([])
            ]);
            if (topologiaRequestRef.current !== solicitudId) return;
            const listaEquipos = Array.isArray(equipos) ? equipos : [];
            const ipObjetivo = String(row?.target_ip || "").trim();
            const nombreDispositivo = normalizarCoincidencia(row?.device_name || row?.entity_type);
            const equipo = listaEquipos.find((item) => ipObjetivo && String(item.ip || "").trim() === ipObjetivo)
                || listaEquipos.find((item) => {
                    const nombreEquipo = normalizarCoincidencia(item.nombre);
                    return nombreDispositivo && (nombreEquipo === nombreDispositivo || nombreEquipo.includes(nombreDispositivo) || nombreDispositivo.includes(nombreEquipo));
                });
            const fallaResuelta = {
                ...row,
                centro_id: centroAlerta?.id || null,
                cliente_id: centroAlerta?.cliente_id || centroCliente?.cliente_id || null,
                equipo_id: equipo?.id_equipo || null,
                equipo_nombre: equipo?.nombre || row?.device_name || row?.entity_type || "Dispositivo"
            };
            setTopologiaFalla({
                cargando: false,
                plantilla: respuestaDiagrama?.plantilla || null,
                falla: fallaResuelta,
                origen: respuestaDiagrama?.origen || null,
                error: respuestaDiagrama?.plantilla ? "" : "No existe una plantilla activa para este centro o cliente."
            });
        } catch (error) {
            if (topologiaRequestRef.current !== solicitudId) return;
            setTopologiaFalla({
                cargando: false,
                plantilla: null,
                falla: row,
                origen: null,
                error: error.response?.data?.error || "No se pudo cargar la topologia de esta alerta."
            });
        }
    };

    const abrirAtajoSoporteDesdeIsmael = (row) => {
        cerrarDetalleCaso();
        resetForm();
        setEditarSoporte(null);
        const esDispositivo = row?.tipo_fuente === "dispositivo";
        const problemaDispositivo = [
            `${row?.device_name || row?.entity_type || "Dispositivo"} sin conexion en ${row?.centro || "centro sin identificar"}.`,
            row?.target_ip ? `IP: ${row.target_ip}.` : "",
            row?.check_type ? `Control: ${row.check_type}.` : ""
        ].filter(Boolean).join(" ");
        setProblema(esDispositivo
            ? problemaDispositivo
            : String(row?.falla_especifica || row?.correo || row?.asunto || "").trim());
        setTipo("remoto");
        setCaseCode(esDispositivo ? "" : String(row?.case_code || "").trim());
        setIsmaelIdOrigen(esDispositivo ? "" : String(row?.id || "").trim());
        setExternalCaseKey(esDispositivo ? String(row?.source_key || "").trim() : "");
        const fechaBase = esDispositivo
            ? row?.offline_since || row?.created_at || new Date().toISOString()
            : row?.hora_llegada || row?.created_at || new Date().toISOString();
        setFechaSoporte(formatearParaInputFecha(esDispositivo ? fechaBase : new Date(fechaBase)));

        const normalizarCentro = (valor) => String(valor || "")
            .trim()
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");
        const centroTexto = normalizarCentro(row?.centro || row?.router_id);
        const clienteTexto = normalizarCentro(row?.cliente);
        if (centroTexto) {
            const centroMatch = (Array.isArray(centros) ? centros : []).find((centro) =>
                normalizarCentro(centro?.nombre) === centroTexto &&
                (!clienteTexto || normalizarCentro(centro?.cliente) === clienteTexto)
            ) || (Array.isArray(centros) ? centros : []).find((centro) =>
                normalizarCentro(centro?.nombre) === centroTexto
            );
            if (centroMatch) {
                setCentroId(String(centroMatch.id));
                setCentroBusqueda(formatearEtiquetaCentro(centroMatch));
            }
        }
        setShowModal(true);
    };

    const handleEliminarCasoExterno = async (row) => {
        if (!esAdmin) return;
        const esDispositivo = row?.tipo_fuente === "dispositivo";
        const origenCaso = esDispositivo ? row?.fuente : "ismael";
        const idCaso = row?.id;
        if (!origenCaso || idCaso === undefined || idCaso === null || idCaso === "") return;

        const nombreCaso = esDispositivo
            ? `${row?.device_name || "dispositivo"} de ${row?.centro || "centro sin identificar"}`
            : `el mensaje ${row?.case_code || "seleccionado"}`;
        if (!window.confirm(`¿Eliminar definitivamente ${nombreCaso}?`)) return;

        const clave = `${origenCaso}:${idCaso}`;
        setEliminandoCasoExterno(clave);
        try {
            await eliminarCasoExternoSoporte(origenCaso, idCaso);
            if (esDispositivo) {
                setFallasDispositivos((actuales) => actuales.filter(
                    (item) => !(String(item?.fuente) === String(origenCaso) && String(item?.id) === String(idCaso))
                ));
            } else {
                setCasosIsmael((actuales) => actuales.filter((item) => String(item?.id) !== String(idCaso)));
            }
            if (
                ismaelSeleccionado &&
                String(ismaelSeleccionado?.id) === String(idCaso) &&
                (esDispositivo
                    ? ismaelSeleccionado?.fuente === origenCaso
                    : ismaelSeleccionado?.tipo_fuente !== "dispositivo")
            ) {
                setIsmaelSeleccionado(null);
            }
        } catch (error) {
            alert(error?.response?.data?.error || "No se pudo eliminar el caso.");
        } finally {
            setEliminandoCasoExterno("");
        }
    };

    const handleGuardarSoporte = async () => {
        if (guardandoSoporte) return;
        const notificar = enviarAvisoTerreno && admiteAvisoTerreno(tipo, estado, fechaCierre);
        if (notificar && (!problema.trim() || !sugerencia.trim())) {
            alert("Completa el problema y la sugerencia para enviar el aviso a soporte.");
            return;
        }

        if (!origen) {
            alert("Selecciona el origen del soporte.");
            return;
        }

        if (cambioEquipo) {
            const cantidad = Number(cantidadEquiposCambiados || 0);
            if (!cantidad || cantidad < 1) {
                alert("Indica una cantidad valida de equipos cambiados.");
                return;
            }
            const listaDetalles = normalizarListaDetalles(cantidad, detalleEquiposCambiadosLista);
            const faltanDetalles = listaDetalles.some((item) => !item);
            if (faltanDetalles) {
                alert("Completa el detalle de cada equipo reemplazado.");
                return;
            }
        }

        const soporteData = {
            centro_id: parseInt(centroId, 10),
            problema,
            tipo,
            origen,
            prioridad: prioridadSoporte,
            fecha_soporte: fechaSoporte,
            solucion,
            sugerencia: sugerencia.trim() || null,
            categoria_falla: categoriaFalla || null,
            subcategoria_falla: subcategoriaFalla || null,
            permiso_trabajo: permisoTrabajo,
            cambio_equipo: cambioEquipo,
            equipo_cambiado: cambioEquipo
                ? construirDetalleCambioEquipo(cantidadEquiposCambiados, detalleEquiposCambiadosLista)
                : null,
            estado,
            case_code: caseCode || null,
            ismael_id_origen: ismaelIdOrigen || null,
            external_case_key: externalCaseKey || null,
            fecha_cierre: fechaCierre || null
        };

        setGuardandoSoporte(true);
        setResultadoAviso(null);
        try {
            const onSuccess = async (resultado) => {
                const id = editarSoporte?.id_soporte || resultado?.id_soporte;
                const aviso = notificar ? await notificarSoporteGuardado(id, enviarAvisoSoporteTerreno) : null;
                resetForm();
                setShowModal(false);
                setResultadoAviso(aviso);
                await refrescarSoportes();
            };

            if (editarSoporte) {
                await modificarSoporte(editarSoporte.id_soporte, soporteData, onSuccess);
            } else {
                await agregarSoporte(soporteData, onSuccess);
            }
        } catch (error) {
            alert(error?.response?.data?.error || "No se pudo guardar el soporte.");
        } finally {
            setGuardandoSoporte(false);
        }
    };

    const handleReintentarAviso = async () => {
        if (!resultadoAviso?.id || !resultadoAviso.reintentable || reintentandoAviso) return;
        setReintentandoAviso(true);
        try {
            setResultadoAviso(await notificarSoporteGuardado(resultadoAviso.id, enviarAvisoSoporteTerreno));
            await refrescarSoportes();
        } finally {
            setReintentandoAviso(false);
        }
    };

    const handleEliminarSoporte = async (id) => {
        if (window.confirm("¿Estás seguro de que deseas eliminar este soporte?")) {
            await borrarSoporte(id, async () => {
                await refrescarSoportes();
            });
        }
    };

    const esElementoInteractivoTabla = (target) =>
        Boolean(target?.closest?.("button, a, input, select, textarea, label, summary, [role='button']"));

    const obtenerScrollTargetTabla = () => {
        const container = tableScrollRef.current;
        if (!container) return null;
        if (container.scrollWidth > container.clientWidth) return container;
        return Array.from(container.querySelectorAll("*")).find(
            (node) => node.scrollWidth > node.clientWidth && window.getComputedStyle(node).overflowX !== "visible"
        ) || container;
    };

    const finalizarArrastreTabla = (event) => {
        const container = tableScrollRef.current;
        if (!container) return;
        const { pointerId } = dragScrollStateRef.current;
        if (pointerId !== null && event?.pointerId === pointerId) {
            container.releasePointerCapture?.(pointerId);
        }
        container.classList.remove("dragging");
        dragScrollStateRef.current = {
            isDown: false,
            isDragging: false,
            startX: 0,
            scrollLeft: 0,
            pointerId: null,
            scrollTarget: null,
            suppressClick: dragScrollStateRef.current.suppressClick
        };
        if (dragScrollStateRef.current.suppressClick) {
            window.setTimeout(() => {
                dragScrollStateRef.current.suppressClick = false;
            }, 150);
        }
    };

    const handleTablePointerDown = (event) => {
        const container = tableScrollRef.current;
        if (!container || esElementoInteractivoTabla(event.target)) return;
        if (event.button !== undefined && event.button !== 0) return;
        const scrollTarget = obtenerScrollTargetTabla();
        if (!scrollTarget) return;
        dragScrollStateRef.current = {
            isDown: true,
            isDragging: false,
            startX: event.clientX,
            scrollLeft: scrollTarget.scrollLeft,
            pointerId: event.pointerId,
            scrollTarget,
            suppressClick: false
        };
    };

    const handleTablePointerMove = (event) => {
        const { scrollTarget } = dragScrollStateRef.current;
        if (!scrollTarget || !dragScrollStateRef.current.isDown) return;
        if (dragScrollStateRef.current.pointerId !== event.pointerId) return;
        const walk = event.clientX - dragScrollStateRef.current.startX;
        if (!dragScrollStateRef.current.isDragging) {
            if (Math.abs(walk) < 8) return;
            tableScrollRef.current?.setPointerCapture?.(event.pointerId);
            tableScrollRef.current?.classList.add("dragging");
            dragScrollStateRef.current.isDragging = true;
            dragScrollStateRef.current.suppressClick = true;
        }
        event.preventDefault();
        scrollTarget.scrollLeft = dragScrollStateRef.current.scrollLeft - walk;
    };

    const handleTableClickCapture = (event) => {
        if (!dragScrollStateRef.current.suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        dragScrollStateRef.current.suppressClick = false;
    };

    const handleEditarSoporte = (soporte) => {
        setEditarSoporte(soporte);
        setCentroId(soporte.centro.id_centro);
        setCentroBusqueda(
            formatearEtiquetaCentro({
                id: soporte.centro.id_centro,
                nombre: soporte.centro.nombre || "Centro",
                cliente: soporte.centro.cliente,
                estado: soporte.centro.estado
            })
        );
        setProblema(soporte.problema);
        setTipo(soporte.tipo);
        setOrigen((soporte.origen || "cliente").toLowerCase());
        setPrioridadSoporte((soporte.prioridad || "media").toLowerCase());
        const fechaSoporteNormalizada = formatearParaInputFecha(soporte.fecha_soporte);
        const fechaCierreNormalizada = formatearParaInputFecha(soporte.fecha_cierre);
        setFechaSoporte(fechaSoporteNormalizada);
        setSolucion(soporte.solucion);
        setSugerencia(soporte.sugerencia || "");
        setEnviarAvisoTerreno(false);
        const categoriaResuelta = resolverCategoriaExistente(
            soporte.categoria_falla,
            soporte.subcategoria_falla
        );
        setCategoriaFalla(categoriaResuelta.categoria);
        setSubcategoriaFalla(categoriaResuelta.subcategoria);
        setPermisoTrabajo(Boolean(soporte.permiso_trabajo));
        setCambioEquipo(soporte.cambio_equipo);
        setCaseCode(String(soporte.case_code || ""));
        setIsmaelIdOrigen(String(soporte.ismael_id_origen || ""));
        setExternalCaseKey(String(soporte.external_case_key || ""));
        const detalleCambio = parsearDetalleCambioEquipo(soporte.equipo_cambiado);
        setCantidadEquiposCambiados(detalleCambio.cantidad || "");
        setDetalleEquiposCambiadosLista(detalleCambio.detalles || []);
        const errorFechas = validarRelacionFechas(fechaSoporteNormalizada, fechaCierreNormalizada);
        setErrorFechaCierre(errorFechas);
        if (!errorFechas && fechaCierreNormalizada) {
            setEstado('resuelto');
        } else {
            setEstado(soporte.estado || 'pendiente');
        }
        setFechaCierre(fechaCierreNormalizada);
        setShowModal(true);
    };

    const abrirDiagramaSoporte = async (soporte) => {
        const centroIdSoporte = soporte?.centro?.id_centro;
        setDiagramaConsulta({
            abierto: true,
            cargando: true,
            error: "",
            plantilla: null,
            origen: null,
            soporte
        });

        if (!centroIdSoporte) {
            setDiagramaConsulta((actual) => ({
                ...actual,
                cargando: false,
                error: "Este soporte no tiene un centro asociado."
            }));
            return;
        }

        try {
            const respuesta = await resolverPlantillaDiagrama({ centroId: centroIdSoporte });
            setDiagramaConsulta((actual) => ({
                ...actual,
                cargando: false,
                plantilla: respuesta?.plantilla || null,
                origen: respuesta?.origen || null,
                error: respuesta?.plantilla ? "" : "No existe una plantilla activa para este centro o cliente."
            }));
        } catch (error) {
            setDiagramaConsulta((actual) => ({
                ...actual,
                cargando: false,
                error: error.response?.data?.error || "No se pudo cargar el diagrama."
            }));
        }
    };

    const formatearFecha = (fecha) => {
        if (!fecha) return "";
        const fechaObj = parseFechaLocal(fecha);
        if (!fechaObj) return "";
        const dia = String(fechaObj.getDate()).padStart(2, "0");
        const mes = String(fechaObj.getMonth() + 1).padStart(2, "0");
        const anio = fechaObj.getFullYear();
        return `${dia}/${mes}/${anio}`;
    };

    const formatearFechaHora = (fecha) => {
        if (!fecha) return "-";
        const f = new Date(fecha);
        if (Number.isNaN(f.getTime())) return "-";
        const dd = String(f.getDate()).padStart(2, "0");
        const mm = String(f.getMonth() + 1).padStart(2, "0");
        const yyyy = f.getFullYear();
        const hh = String(f.getHours()).padStart(2, "0");
        const min = String(f.getMinutes()).padStart(2, "0");
        return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
    };

    const formatearParaInputFecha = (fecha) => {
        if (!fecha) return "";
        const fechaObj = parseFechaLocal(fecha);
        if (!fechaObj) {
            return "";
        }
        const dia = String(fechaObj.getDate()).padStart(2, "0");
        const mes = String(fechaObj.getMonth() + 1).padStart(2, "0");
        const anio = fechaObj.getFullYear();
        return `${anio}-${mes}-${dia}`;
    };

    const validarRelacionFechas = (inicio, fin) => {
        if (!fin) return "";
        const fechaFin = new Date(fin);
        if (Number.isNaN(fechaFin.getTime())) {
            return "Fecha de cierre inválida";
        }
        if (inicio) {
            const fechaInicio = new Date(inicio);
            if (!Number.isNaN(fechaInicio.getTime()) && fechaFin < fechaInicio) {
                return "La fecha de cierre no puede ser anterior a la fecha del soporte";
            }
        }
        return "";
    };

    const handleFechaSoporteChange = (valor) => {
        setFechaSoporte(valor);
        if (!fechaCierre) {
            setErrorFechaCierre("");
            return;
        }
        const error = validarRelacionFechas(valor, fechaCierre);
        setErrorFechaCierre(error);
        if (error && estado === "resuelto") {
            setEstado("pendiente");
        } else if (!error) {
            setEstado("resuelto");
        }
    };

    const handleFechaCierreChange = (valor) => {
        setFechaCierre(valor);
        if (!valor) {
            setErrorFechaCierre("");
            if (estado === "resuelto") {
                setEstado("pendiente");
            }
            return;
        }
        const error = validarRelacionFechas(fechaSoporte, valor);
        setErrorFechaCierre(error);
        if (!error) {
            setEstado("resuelto");
        } else if (estado === "resuelto") {
            setEstado("pendiente");
        }
    };

    const formatearDias = (dias) => `${dias} dia${dias === 1 ? "" : "s"}`;

    const calcularDiasAbiertos = (soporte) => {
        if (!soporte.fecha_soporte) return 0;
        const inicio = parseFechaLocal(soporte.fecha_soporte);
        if (!inicio || Number.isNaN(inicio.getTime())) return 0;
        const fin = soporte.fecha_cierre ? parseFechaLocal(soporte.fecha_cierre) : new Date();
        if (!fin || Number.isNaN(fin.getTime())) return 0;
        fin.setHours(0, 0, 0, 0);
        const diferencia = fin.getTime() - inicio.getTime();
        return Math.max(0, Math.floor(diferencia / (1000 * 60 * 60 * 24)));
    };

    const renderEstado = (valor) => {
        const estadoNormalizado = (valor || "pendiente").toLowerCase();
        const baseClass = "d-inline-flex align-items-center px-2 py-1 rounded-pill font-weight-semibold";
        const variantes = {
            pendiente: { clase: `${baseClass} bg-danger text-white`, icono: "fas fa-exclamation-circle" },
            en_proceso: { clase: `${baseClass} bg-warning text-dark`, icono: "fas fa-exclamation-triangle", label: "Alerta" },
            resuelto: { clase: `${baseClass} bg-success text-white`, icono: "fas fa-check-circle" }
        };
        const { clase, icono } = {
            ...variantes.pendiente,
            ...variantes[estadoNormalizado]
        };
        const label = variantes[estadoNormalizado]?.label || estadoNormalizado.replace("_", " ");
        return (
            <span className={clase}>
                <i className={`${icono} mr-2`}></i>
                {label}
            </span>
        );
    };

    const datosOrdenados = useMemo(() => {
        const ordenados = [...soportesFiltradosBusqueda].sort((a, b) => {
            const fechaA = parseFechaLocal(a.fecha_soporte)?.getTime() || 0;
            const fechaB = parseFechaLocal(b.fecha_soporte)?.getTime() || 0;
            return fechaB - fechaA;
        });
        return ordenados.map((item, index) => ({
            ...item,
            rowNumber: index + 1
        }));
    }, [soportesFiltradosBusqueda]);

    const casosIsmaelOrdenados = useMemo(() => {
        const casosCorreo = (Array.isArray(casosIsmael) ? casosIsmael : []).map((caso) => ({
            ...caso,
            tipo_fuente: "ismael"
        }));
        const casosDispositivo = (Array.isArray(fallasDispositivos) ? fallasDispositivos : []).map((caso) => ({
            ...caso,
            tipo_fuente: "dispositivo"
        }));
        return [...casosDispositivo, ...casosCorreo].sort((a, b) => {
            const recuperadoA = a?.tipo_fuente === "dispositivo" && a?.estado === "recuperado" ? 1 : 0;
            const recuperadoB = b?.tipo_fuente === "dispositivo" && b?.estado === "recuperado" ? 1 : 0;
            if (recuperadoA !== recuperadoB) return recuperadoA - recuperadoB;
            const fa = new Date(a?.offline_since || a?.created_at || a?.updated_at || a?.hora_llegada || 0).getTime();
            const fb = new Date(b?.offline_since || b?.created_at || b?.updated_at || b?.hora_llegada || 0).getTime();
            return fb - fa;
        });
    }, [casosIsmael, fallasDispositivos]);
    const totalPaginasIsmael = Math.max(1, Math.ceil(casosIsmaelOrdenados.length / registrosPorTarjeta));
    const casosIsmaelPreview = useMemo(() => {
        const inicio = (paginaIsmael - 1) * registrosPorTarjeta;
        return casosIsmaelOrdenados.slice(inicio, inicio + registrosPorTarjeta);
    }, [casosIsmaelOrdenados, paginaIsmael]);

    const pendientesAbiertos = useMemo(() => {
        return soportesFiltrados
            .filter((soporte) => {
                const estado = (soporte.estado || "pendiente").toLowerCase();
                return estado === "pendiente" || estado === "en_proceso";
            })
            .map((soporte) => ({
                ...soporte,
                diasAbiertos: calcularDiasAbiertos(soporte)
            }))
            .sort((a, b) => b.diasAbiertos - a.diasAbiertos);
    }, [soportesFiltrados]);

    const totalPendientesAbiertos = pendientesAbiertos.length;
    const totalPendientesRemotos = useMemo(
        () =>
            pendientesAbiertos.filter(
                (soporte) => String(soporte.tipo || "").toLowerCase() === "remoto"
            ).length,
        [pendientesAbiertos]
    );
    const totalPendientesTerreno = useMemo(
        () =>
            pendientesAbiertos.filter(
                (soporte) => String(soporte.tipo || "").toLowerCase() === "terreno"
            ).length,
        [pendientesAbiertos]
    );
    const totalResueltosPeriodo = useMemo(
        () =>
            soportesFiltrados.filter((soporte) => {
                const estado = String(soporte.estado || "").toLowerCase();
                return estado === "resuelto" || estado === "finalizado";
            }).length,
        [soportesFiltrados]
    );
    const totalResueltasRemotasPeriodo = useMemo(
        () =>
            soportesFiltrados.filter((soporte) => {
                const estado = String(soporte.estado || "").toLowerCase();
                const tipoSoporte = String(soporte.tipo || "").toLowerCase();
                return (estado === "resuelto" || estado === "finalizado") && tipoSoporte === "remoto";
            }).length,
        [soportesFiltrados]
    );
    const totalResueltasRemotasGeneral = useMemo(
        () =>
            soportes.filter((soporte) => {
                const estado = String(soporte.estado || "").toLowerCase();
                const tipoSoporte = String(soporte.tipo || "").toLowerCase();
                return (estado === "resuelto" || estado === "finalizado") && tipoSoporte === "remoto";
            }).length,
        [soportes]
    );
    const totalResueltasTerrenoPeriodo = useMemo(
        () =>
            soportesFiltrados.filter((soporte) => {
                const estado = String(soporte.estado || "").toLowerCase();
                const tipoSoporte = String(soporte.tipo || "").toLowerCase();
                return (estado === "resuelto" || estado === "finalizado") && tipoSoporte === "terreno";
            }).length,
        [soportesFiltrados]
    );
    const totalResueltasTerrenoGeneral = useMemo(
        () =>
            soportes.filter((soporte) => {
                const estado = String(soporte.estado || "").toLowerCase();
                const tipoSoporte = String(soporte.tipo || "").toLowerCase();
                return (estado === "resuelto" || estado === "finalizado") && tipoSoporte === "terreno";
            }).length,
        [soportes]
    );
    const totalPendientesPrioritarios = useMemo(
        () =>
            pendientesAbiertos.filter(
                (soporte) => String(soporte.estado || "pendiente").toLowerCase() === "pendiente"
            ).length,
        [pendientesAbiertos]
    );
    const totalAlertasPrioritarias = useMemo(
        () =>
            pendientesAbiertos.filter(
                (soporte) => String(soporte.estado || "").toLowerCase() === "en_proceso"
            ).length,
        [pendientesAbiertos]
    );
    const pendientesCriticos = useMemo(
        () => {
            const inicio = (paginaPendientes - 1) * registrosPorTarjeta;
            return pendientesAbiertos.slice(inicio, inicio + registrosPorTarjeta);
        },
        [pendientesAbiertos, paginaPendientes]
    );
    const totalPaginasPendientes = Math.max(1, Math.ceil(pendientesAbiertos.length / registrosPorTarjeta));

    useEffect(() => {
        setPaginaIsmael((pagina) => Math.min(pagina, totalPaginasIsmael));
    }, [totalPaginasIsmael]);

    useEffect(() => {
        setPaginaPendientes((pagina) => Math.min(pagina, totalPaginasPendientes));
    }, [totalPaginasPendientes]);

    const columns = [
        {
            name: "N",
            selector: (row) => row.rowNumber,
            sortable: true,
            width: "46px",
            sortFunction: (rowA, rowB) => rowA.rowNumber - rowB.rowNumber
        },
        { name: "Centro", selector: (row) => row.centro?.nombre || "No asignado", sortable: true, wrap: true, minWidth: "145px" },
        { name: "Cliente", selector: (row) => row.centro?.cliente || "-", sortable: true, wrap: true, minWidth: "135px" },
        { name: "Problema", selector: (row) => row.problema, sortable: true, wrap: true, minWidth: "270px",
            cell: (row) => <div className="py-2">
                <span>{row.problema}</span>
                {row.sugerencia && <details className="soporte-sugerencia-detalle mt-2">
                    <summary>Sugerencia de terreno</summary><p>{row.sugerencia}</p>
                </details>}
                <EstadoAvisoTerreno aviso={row.aviso_terreno} />
            </div> },
        { name: "Tipo", selector: (row) => obtenerEtiquetaTipoSoporte(row.tipo), sortable: true, width: "90px" },
        {
            name: "Origen",
            selector: (row) => row.origen || "cliente",
            sortable: true,
            width: "90px",
            cell: (row) => {
                const valor = String(row.origen || "cliente").toLowerCase();
                const esOrca = valor === "orca";
                const esTerceros = valor === "terceros";
                return (
                    <span className={`badge badge-pill ${esOrca ? "badge-info" : esTerceros ? "badge-warning" : "badge-secondary"}`}>
                        {esOrca ? "Orca" : esTerceros ? "Terceros" : "Cliente"}
                    </span>
                );
            }
        },
        {
            name: "Prioridad",
            selector: (row) => row.prioridad || "media",
            sortable: true,
            width: "98px",
            cell: (row) => {
                const valor = String(row.prioridad || "media").toLowerCase();
                const clases = {
                    alta: "badge-danger",
                    media: "badge-warning",
                    baja: "badge-success"
                };
                return (
                    <span className={`badge badge-pill ${clases[valor] || "badge-secondary"}`}>
                        {valor.charAt(0).toUpperCase() + valor.slice(1)}
                    </span>
                );
            }
        },
        {
            id: "fechaSoporte",
            name: "Fecha",
            selector: (row) => row.fecha_soporte,
            sortable: true,
            sortFunction: (rowA, rowB) =>
                (parseFechaLocal(rowA.fecha_soporte)?.getTime() || 0) -
                (parseFechaLocal(rowB.fecha_soporte)?.getTime() || 0),
            cell: (row) => formatearFecha(row.fecha_soporte),
            width: "100px"
        },
        {
            name: "D\u00edas",
            selector: (row) => calcularDiasAbiertos(row),
            sortable: true,
            width: "95px",
            cell: (row) =>
                row.fecha_cierre
                    ? `${calcularDiasAbiertos(row)} d\u00edas`
                    : "Alerta"
        },
        {
            name: "Estado",
            selector: (row) => row.estado || "pendiente",
            sortable: true,
            cell: (row) => (
                <div>
                    {renderEstado(row.estado)}
                    {esAdmin && !!String(row.creado_por_nombre || "").trim() && (
                        <small className="soporte-creador">
                            <i className="fas fa-user" aria-hidden="true" /> Creado por:
                            <strong>{row.creado_por_nombre}</strong>
                        </small>
                    )}
                    {!!String(row.case_code || "").trim() && (
                        <small className="d-block text-muted mt-1">Case: {row.case_code}</small>
                    )}
                </div>
            ),
            width: esAdmin ? "165px" : "128px"
        },
        { name: "Cierre", selector: (row) => (row.fecha_cierre ? formatearFecha(row.fecha_cierre) : "-"), sortable: true, width: "100px" },
        { name: "Solucion", selector: (row) => row.solucion || "-", sortable: true, wrap: true, minWidth: "235px" },
        {
            name: "Categoria",
            selector: (row) => row.categoria_falla || "-",
            sortable: true,
            wrap: true,
            cell: (row) => (
                <div className="soporte-categoria-cell">
                    <span>{row.categoria_falla || "-"}</span>
                    {row.permiso_trabajo ? (
                        <span
                            className="permiso-trabajo-badge activo"
                            title="Requiere permiso de trabajo"
                        >
                            <i className="fas fa-user-cog"></i>
                        </span>
                    ) : null}
                </div>
            ),
            minWidth: "140px"
        },
        { name: "Subcategoria", selector: (row) => row.subcategoria_falla || "-", sortable: true, wrap: true, minWidth: "155px" },
        {
            id: "cambioEquipo",
            name: "Cambio equipo",
            selector: (row) => row.cambio_equipo,
            sortable: true,
            width: "100px",
            cell: (row) => (
                <span
                    className="change-icon"
                    title={row.cambio_equipo ? "Cambio de equipo realizado" : "Sin cambio de equipo"}
                >
                    <i className={`fas ${row.cambio_equipo ? "fa-check text-success" : "fa-times text-danger"}`}></i>
                </span>
            )
        },
        {
            id: "equipoCambiado",
            name: "Equipo cambiado",
            selector: (row) => obtenerNombresCambioSoporte(row),
            sortable: true,
            wrap: true,
            minWidth: "240px",
            cell: (row) => {
                if (!row.cambio_equipo) return "-";
                const cantidad = obtenerCantidadCambioSoporte(row);
                const nombres = obtenerNombresCambioSoporte(row);
                return (
                    <div className="d-flex align-items-center flex-wrap" title={nombres}>
                        <span className="badge badge-primary mr-2">{cantidad}</span>
                        <span className="text-uppercase">{nombres}</span>
                    </div>
                );
            }
        },
        {
            name: "Acciones",
            cell: (row) => (
                <div className="soporte-table-actions">
                    <button className="btn btn-outline-info btn-sm" onClick={() => abrirDiagramaSoporte(row)} title="Ver diagrama">
                        <i className="fas fa-project-diagram"></i>
                    </button>
                    <button className="btn btn-warning btn-sm" onClick={() => handleEditarSoporte(row)} title="Editar soporte">
                        <i className="fas fa-edit"></i>
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleEliminarSoporte(row.id_soporte)} title="Eliminar soporte">
                        <i className="fas fa-trash-alt"></i>
                    </button>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            width: "164px"
        }
    ].filter((columna) => mostrarCambiosEquipo || !["cambioEquipo", "equipoCambiado"].includes(columna.id));

    const dataTableStyles = {
        table: {
            style: {
                border: "1px solid #e2e8f0",
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: "0 10px 25px rgba(15, 23, 42, 0.08)"
            }
        },
        headRow: {
            style: {
                backgroundColor: "#e2e8f0",
                borderBottomColor: "#cbd5f5",
                borderBottomWidth: "1px",
                borderBottomStyle: "solid"
            }
        },
        headCells: {
            style: {
                backgroundColor: "#e2e8f0",
                color: "#0f172a",
                fontSize: "0.85rem",
                fontWeight: 600
            }
        },
        rows: {
            style: {
                minHeight: "62px",
                borderBottomColor: "#e2e8f0",
                borderBottomWidth: "1px",
                borderBottomStyle: "solid"
            },
            highlightOnHoverStyle: {
                backgroundColor: "#ecfeff",
                borderBottomColor: "#bae6fd"
            }
        },
        cells: {
            style: {
                borderRightColor: "#eef2ff",
                borderRightWidth: "1px",
                borderRightStyle: "solid"
            }
        },
        pagination: {
            style: {
                borderTop: "none",
                padding: "1rem",
                color: "#475569"
            }
        }
    };

    const metricCards = [
        {
            title: `Soportes (${descripcionPeriodo})`,
            value: totalSoportesPeriodo,
            subtitle: `Historico: ${totalSoportesGeneral}`,
            icon: "fas fa-clipboard-check",
            variant: "gradient-blue"
        },
        {
            title: "Cambios de equipo",
            value: totalCambiosEquiposPeriodo,
            subtitle: `Historico: ${totalCambiosEquiposGeneral}`,
            icon: "fas fa-sync-alt",
            variant: "gradient-orange"
        },
        {
            title: "Clientes atendidos",
            value: totalClientesPeriodo,
            subtitle: `Historico: ${totalClientesGeneral}`,
            icon: "fas fa-users",
            variant: "gradient-blue"
        },
        {
            title: "Centros gestionados",
            value: totalCentrosPeriodo,
            subtitle: `Historico: ${totalCentrosGeneral}`,
            icon: "fas fa-network-wired",
            variant: "gradient-blue"
        },
        {
            title: "",
            value: totalResueltasRemotasPeriodo,
            secondaryValue: totalResueltasTerrenoPeriodo,
            valueLabel: "Remotas",
            valueIcon: "fas fa-check-circle",
            secondaryLabel: "Terreno",
            subtitle: `Historico ${totalResueltasRemotasGeneral} / ${totalResueltasTerrenoGeneral}`,
            icon: "fas fa-headset",
            variant: "gradient-green"
        }
    ];

    return (
        <div className="container-fluid soporte-page">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="section-title d-flex align-items-center mb-0">
                    <i className="fas fa-headset text-primary mr-2"></i>
                    Historial de Soporte
                </h3>
                <button className="btn btn-outline-secondary" onClick={() => navigate("/soporte/detalle")}>
                    Ir a detalle
                </button>
            </div>

            <ResultadoAvisoTerreno resultado={resultadoAviso} onReintentar={handleReintentarAviso} ocupado={reintentandoAviso} />
            <div className="card mb-4 filter-card">
                <div className="card-body">
                    <div className="row align-items-end">
                        <div className="col-lg-6 col-md-7">
                            <label className="font-weight-semibold text-muted">Periodo de análisis</label>
                            <select
                                className="form-control"
                                value={filtroPeriodo}
                                onChange={(e) => setFiltroPeriodo(e.target.value)}
                            >
                                <option value="mes-actual">Mes actual</option>
                                <option value="mes-anterior">Mes anterior</option>
                                <option value="anio-actual">Año actual</option>
                                <option value="anio-anterior">Año anterior</option>
                                <option value="personalizado">Personalizado</option>
                            </select>
                            {filtroPeriodo === "personalizado" && (
                                <div className="row mt-3">
                                    <div className="col-sm-6">
                                        <small className="text-muted text-uppercase d-block mb-1">Desde</small>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={fechaInicioPersonalizada}
                                            onChange={(e) => setFechaInicioPersonalizada(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-sm-6 mt-3 mt-sm-0">
                                        <small className="text-muted text-uppercase d-block mb-1">Hasta</small>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={fechaFinPersonalizada}
                                            onChange={(e) => setFechaFinPersonalizada(e.target.value)}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="col-lg-6 col-md-5 mt-3 mt-md-0 text-md-right actions-group">
                            <button className="btn btn-primary mr-2" onClick={() => { resetForm(); setShowModal(true); }}>
                                <i className="fas fa-plus-circle mr-2"></i>
                                Crear Soporte
                            </button>
                            <button className="btn btn-outline-primary" onClick={refrescarSoportes}>
                                <i className="fas fa-sync mr-2"></i>
                                Actualizar lista
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row mb-4">
                {metricCards.map((card) => (
                    <div className="col-md-6 col-xl mb-3" key={card.title}>
                        <div className={`card metric-card text-white ${card.variant}`}>
                            <div className="card-body">
                                {card.title ? <h5 className="mb-1">{card.title}</h5> : null}
                                {typeof card.secondaryValue !== "undefined" ? (
                                    <div className="metric-split-values">
                                        <div className="metric-split-block">
                                            <small>
                                                {card.valueIcon ? <i className={`${card.valueIcon} mr-1`} /> : null}
                                                {card.valueLabel}
                                            </small>
                                            <h2 className="mb-0">{card.value}</h2>
                                        </div>
                                        <div className="metric-split-divider" />
                                        <div className="metric-split-block">
                                            <small>
                                                {card.secondaryIcon ? <i className={`${card.secondaryIcon} mr-1`} /> : null}
                                                {card.secondaryLabel}
                                            </small>
                                            <h2 className="mb-0">{card.secondaryValue}</h2>
                                        </div>
                                    </div>
                                ) : (
                                    <h2 className="mb-0">{card.value}</h2>
                                )}
                                <div className="metric-icon">
                                    <i className={card.icon}></i>
                                </div>
                                <span className="metric-subtitle">{card.subtitle}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="row mb-4">
                <div className="col-lg-6 mb-3">
                    <div className="card status-summary-card h-100 ismael-today-card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div>
                                    <h5 className="text-uppercase text-muted mb-1">Estado de casos de hoy</h5>
                                    <small className="text-secondary">Correos y monitoreo de dispositivos</small>
                                </div>
                                <span className="badge badge-pill badge-success" title="Resueltos del periodo">
                                    <i className="fas fa-check-circle mr-1"></i>
                                    Resueltos: {totalResueltosPeriodo}
                                </span>
                            </div>
                            <div className="d-flex align-items-center justify-content-between mb-2">
                                <span className="badge bg-soft-success text-success">
                                    <i className="fas fa-database mr-1"></i>
                                    Registros
                                </span>
                                <span className="ismael-total-chip" title="Total de casos disponibles">
                                    Total: {casosIsmaelOrdenados.length}
                                </span>
                            </div>
                            <div className="ismael-preview-list">
                                {casosIsmaelPreview.map((row) => (
                                    <div
                                        key={row.source_key || row.id || `${row.case_code}-${row.created_at}`}
                                        className={`ismael-preview-item ${row.tipo_fuente === "dispositivo" ? `device-failure-item device-client-${row.fuente || "otro"}` : ""}`}
                                    >
                                        <div className="d-flex justify-content-between align-items-center">
                                            <strong className="text-truncate mr-2">
                                                {row.tipo_fuente === "dispositivo"
                                                    ? (row.device_name || row.entity_type || "Dispositivo")
                                                    : (row.case_code || "Sin código")}
                                            </strong>
                                            <div className="d-flex align-items-center">
                                                <button
                                                    className="btn btn-outline-primary btn-sm mr-1"
                                                    title="Ver detalle"
                                                    onClick={() => abrirDetalleCaso(row)}
                                                >
                                                    <i className="fas fa-eye" />
                                                </button>
                                                <button
                                                    className="btn btn-outline-success btn-sm"
                                                    title="Crear soporte"
                                                    onClick={() => abrirAtajoSoporteDesdeIsmael(row)}
                                                >
                                                    <i className="fas fa-plus" />
                                                </button>
                                                {esAdmin && (
                                                    <button
                                                        className="btn btn-outline-danger btn-sm ml-1"
                                                        title="Eliminar caso"
                                                        disabled={eliminandoCasoExterno === `${row.tipo_fuente === "dispositivo" ? row.fuente : "ismael"}:${row.id}`}
                                                        onClick={() => handleEliminarCasoExterno(row)}
                                                    >
                                                        <i className={`fas ${eliminandoCasoExterno === `${row.tipo_fuente === "dispositivo" ? row.fuente : "ismael"}:${row.id}` ? "fa-spinner fa-spin" : "fa-trash"}`} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <small className="text-muted d-block text-truncate">
                                            {row.tipo_fuente === "dispositivo" ? (
                                                <>
                                                    <strong>{row.cliente || "Cliente"}</strong>
                                                    {` · ${row.centro || "Centro sin identificar"}`}
                                                </>
                                            ) : (
                                                `Centro: ${row.centro || "-"}`
                                            )}
                                        </small>
                                        {row.tipo_fuente === "dispositivo" ? (
                                            <div className="device-failure-meta">
                                                <span>{row.target_ip || "Sin IP"}</span>
                                                <span className={`device-failure-state ${row.estado === "recuperado" ? "is-recovered" : "is-active"}`}>
                                                    {row.estado === "recuperado" ? "Recuperado hoy" : "Falla activa"}
                                                </span>
                                            </div>
                                        ) : (
                                            <>
                                            <small className="ismael-claim-date">
                                                <i className="far fa-clock" aria-hidden="true" />
                                                <span>
                                                    {row.hora_llegada ? "Reclamo" : "Registro"}: {formatearFechaHora(row.hora_llegada || row.created_at).replace(/\s+(\d{2}:\d{2})$/, " a las $1")}
                                                </span>
                                            </small>
                                            <small className="text-muted d-block text-truncate">
                                                Problema: {row.falla_especifica || row.correo || row.asunto || "-"}
                                            </small>
                                            </>
                                        )}
                                    </div>
                                ))}
                                {!casosIsmaelPreview.length && (
                                    <small className="text-muted">Sin casos externos disponibles.</small>
                                )}
                            </div>
                            {casosIsmaelOrdenados.length > registrosPorTarjeta && (
                                <div className="card-pagination mt-3">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        disabled={paginaIsmael <= 1}
                                        onClick={() => setPaginaIsmael((pagina) => Math.max(1, pagina - 1))}
                                    >
                                        <i className="fas fa-chevron-left mr-1"></i>
                                        Anterior
                                    </button>
                                    <span>
                                        Pagina {paginaIsmael} de {totalPaginasIsmael}
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        disabled={paginaIsmael >= totalPaginasIsmael}
                                        onClick={() => setPaginaIsmael((pagina) => Math.min(totalPaginasIsmael, pagina + 1))}
                                    >
                                        Siguiente
                                        <i className="fas fa-chevron-right ml-1"></i>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="col-lg-6 mb-3">
                    <div className="card status-summary-card h-100">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div>
                                    <h5 className="text-uppercase text-muted mb-1">Pendientes prioritarios</h5>
                                    <small className="text-secondary">Casos mas antiguos sin cierre</small>
                                </div>
                                <div className="d-flex align-items-center">
                                    <span className="badge badge-pill badge-dark mr-2" title="Total de fallas abiertas">
                                        <i className="fas fa-layer-group mr-1"></i>
                                        Total fallas abiertas: {totalPendientesAbiertos}
                                    </span>
                                    <span className="badge badge-pill badge-danger mr-2" title="Pendientes">
                                        Pendientes: {totalPendientesPrioritarios}
                                    </span>
                                    <span className="badge badge-pill badge-warning text-dark mr-2" title="Alertas">
                                        Alertas: {totalAlertasPrioritarias}
                                    </span>
                                    <span className="badge badge-pill badge-info mr-2" title="Pendientes remotos">
                                        Remotas: {totalPendientesRemotos}
                                    </span>
                                    <span className="badge badge-pill badge-primary mr-2" title="Pendientes terreno">
                                        Terreno: {totalPendientesTerreno}
                                    </span>
                                </div>
                            </div>
                            {pendientesCriticos.length ? (
                                <ul className="list-unstyled mb-0">
                                    {pendientesCriticos.map((soporte) => (
                                        <li
                                            key={soporte.id_soporte}
                                            className={`urgent-item ${String(soporte.estado || "").toLowerCase() === "en_proceso" ? "urgent-item-alert" : ""}`}
                                            onClick={() => handleEditarSoporte(soporte)}
                                        >
                                            <div>
                                                <div className="d-flex align-items-center flex-wrap">
                                                    <strong>{soporte.centro?.nombre || "Centro sin nombre"}</strong>
                                                    <span
                                                        className={`urgent-type-chip ml-2 ${obtenerClaseTipoSoporte(soporte.tipo)}`}
                                                    >
                                                        {obtenerEtiquetaTipoSoporte(soporte.tipo)}
                                                    </span>
                                                </div>
                                                {soporte.problema && (
                                                    <small className="text-danger ml-2 font-italic">
                                                        {soporte.problema}
                                                    </small>
                                                )}
                                                <small className="d-block text-muted">
                                                    {soporte.centro?.cliente || "Cliente sin nombre"} · {formatearFecha(soporte.fecha_soporte)}
                                                </small>
                                            </div>
                                            <span className={`urgent-days ${String(soporte.estado || "").toLowerCase() === "en_proceso" ? "urgent-days-alert" : ""}`}>
                                                <i className="fas fa-clock mr-1"></i>
                                                {formatearDias(soporte.diasAbiertos)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-muted mb-0">No hay pendientes en este periodo.</p>
                            )}
                            {pendientesAbiertos.length > registrosPorTarjeta && (
                                <div className="card-pagination mt-3">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        disabled={paginaPendientes <= 1}
                                        onClick={() => setPaginaPendientes((pagina) => Math.max(1, pagina - 1))}
                                    >
                                        <i className="fas fa-chevron-left mr-1"></i>
                                        Anterior
                                    </button>
                                    <span>
                                        Pagina {paginaPendientes} de {totalPaginasPendientes}
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        disabled={paginaPendientes >= totalPaginasPendientes}
                                        onClick={() => setPaginaPendientes((pagina) => Math.min(totalPaginasPendientes, pagina + 1))}
                                    >
                                        Siguiente
                                        <i className="fas fa-chevron-right ml-1"></i>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="card data-table-card">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="text-muted">
                            Mostrando {datosOrdenados.length} resultado{datosOrdenados.length === 1 ? "" : "s"}
                        </small>
                    </div>
                    <div className="row mb-3">
                        <div className="col-lg-6">
                            <small className="text-muted text-uppercase d-block mb-1">Buscar soporte</small>
                            <input
                                type="text"
                                className="form-control"
                                placeholder={esAdmin
                                    ? "Centro, cliente, problema, case code, categoria o creador"
                                    : "Centro, cliente, problema, case code o categoria"}
                                value={filtroNombre}
                                onChange={(e) => setFiltroNombre(e.target.value)}
                            />
                        </div>
                        <div className="col-lg-3 mt-3 mt-lg-0">
                            <small className="text-muted text-uppercase d-block mb-1">Rango desde</small>
                            <input
                                type="date"
                                className="form-control"
                                value={fechaInicioBusqueda}
                                onChange={(e) => setFechaInicioBusqueda(e.target.value)}
                            />
                        </div>
                        <div className="col-lg-3 mt-3 mt-lg-0">
                            <small className="text-muted text-uppercase d-block mb-1">Rango hasta</small>
                            <div className="soporte-range-actions">
                                <input
                                    type="date"
                                    className="form-control"
                                    value={fechaFinBusqueda}
                                    onChange={(e) => setFechaFinBusqueda(e.target.value)}
                                />
                                <button
                                    type="button"
                                    className={`btn ${mostrarCambiosEquipo ? "btn-primary" : "btn-outline-primary"}`}
                                    onClick={() => setMostrarCambiosEquipo((valor) => !valor)}
                                    title={mostrarCambiosEquipo ? "Ocultar columnas de cambio de equipo" : "Ver cambios de equipo"}
                                >
                                    <i className={`fas ${mostrarCambiosEquipo ? "fa-eye-slash" : "fa-exchange-alt"}`}></i>
                                    <span>{mostrarCambiosEquipo ? "Ocultar cambios" : "Ver cambios"}</span>
                                </button>
                            </div>
                        </div>
                    </div>
	                    <div
	                        ref={tableScrollRef}
	                        className={`table-drag-scroll ${mostrarCambiosEquipo ? "show-equipment-changes" : ""}`}
	                        onPointerDown={handleTablePointerDown}
	                        onPointerMove={handleTablePointerMove}
		                        onPointerUp={finalizarArrastreTabla}
		                        onPointerCancel={finalizarArrastreTabla}
		                        onLostPointerCapture={finalizarArrastreTabla}
		                        onClickCapture={handleTableClickCapture}
		                    >
                        <DataTable
                            columns={columns}
                            data={datosOrdenados}
                            progressPending={loading}
                            pagination
                            highlightOnHover
                            pointerOnHover
                            persistTableHead
                            defaultSortFieldId="fechaSoporte"
                            defaultSortAsc={false}
                            customStyles={dataTableStyles}
                            noDataComponent="Sin registros para el periodo seleccionado"
                        />
                    </div>
                    <small className="text-muted d-block mt-2">Arrastra la tabla hacia los lados para ver todas las columnas.</small>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal show" style={{ display: "block" }}>
                    <div className="modal-dialog modal-lg modal-dialog-scrollable">
                        <div className="modal-content soporte-modal shadow-lg border-0">
                            <div className="modal-header">
                                <h5>{editarSoporte ? "Editar Soporte" : "Crear Soporte"}</h5>
                                <div className="d-flex align-items-center">
                                    {editarSoporte && (
                                        <button type="button" className="btn btn-outline-info btn-sm mr-3" onClick={() => abrirDiagramaSoporte(editarSoporte)}>
                                            <i className="fas fa-project-diagram mr-1"></i>
                                            Ver diagrama
                                        </button>
                                    )}
                                    <button className="close" disabled={guardandoSoporte} onClick={() => setShowModal(false)}>
                                        &times;
                                    </button>
                                </div>
                            </div>
                            <div className="modal-body soporte-form-body">
                                <div className="form-group">
                                    <label className="text-muted small font-weight-semibold">Centro</label>
                                    <div className="centro-sugerencia-wrap">
                                        <input
                                            type="text"
                                            className={`form-control ${claseEstadoCentroInput}`}
                                            placeholder="ID - Centro (Cliente) · Estado"
                                            value={centroBusqueda}
                                            onFocus={() => setMostrarSugerenciasCentro(true)}
                                            onBlur={() => setTimeout(() => setMostrarSugerenciasCentro(false), 120)}
                                            onChange={(e) => handleCentroBusquedaChange(e.target.value)}
                                        />
                                        {mostrarSugerenciasCentro && !!centrosSugeridos.length && (
                                            <div className="centro-sugerencias-list">
                                                {centrosSugeridos.map((centro) => {
                                                    const estado = String(centro.estado || "sin estado");
                                                    return (
                                                        <button
                                                            type="button"
                                                            key={centro.id}
                                                            className="centro-sugerencia-item"
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            onClick={() => handleSeleccionarCentroSugerido(centro)}
                                                        >
                                                            <span className="centro-sugerencia-main">
                                                                {centro.id} - {centro.nombre}
                                                                {centro.cliente ? ` (${centro.cliente})` : ""}
                                                            </span>
                                                            <span className={`centro-sugerencia-estado estado-${normalizarEstadoCentro(estado) || "sin-estado"}`}>
                                                                {estado}
                                                            </span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                    {!centroId && (
                                        <small className="text-muted">Escribe para buscar y selecciona una opción.</small>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label className="text-muted small font-weight-semibold">Problema reportado</label>
                                    <textarea
                                        placeholder="Describe la falla detectada"
                                        value={problema}
                                        onChange={(e) => setProblema(e.target.value)}
                                        className="form-control"
                                        rows="3"
                                    />
                                </div>

                                <div className="form-row">
                                    <div className="form-group col-md-6">
                                        <label className="text-muted small font-weight-semibold">Tipo de soporte</label>
                                        <select
                                            value={tipo}
                                            onChange={(e) => setTipo(e.target.value)}
                                            className="form-control"
                                        >
                                            <option value="">Tipo</option>
                                            <option value="terreno">Terreno</option>
                                            <option value="remoto">Remoto</option>
                                        </select>
                                    </div>
                                    <div className="form-group col-md-6">
                                        <label className="text-muted small font-weight-semibold">Fecha de creación</label>
                                        <input
                                            type="date"
                                            value={fechaSoporte}
                                            onChange={(e) => handleFechaSoporteChange(e.target.value)}
                                            className="form-control"
                                        />
                                    </div>
                                </div>

	                                <div className="form-row">
                                    <div className="form-group col-md-6">
                                        <label className="text-muted small font-weight-semibold">Origen del reporte</label>
	                                        <select
	                                            value={origen}
	                                            onChange={(e) => setOrigen(e.target.value)}
	                                            className="form-control"
	                                            required
	                                        >
	                                            <option value="cliente">Cliente</option>
	                                            <option value="orca">Orca</option>
	                                            <option value="terceros">Terceros</option>
	                                        </select>
                                    </div>
                                    <div className="form-group col-md-6">
                                        <label className="text-muted small font-weight-semibold">Prioridad</label>
                                        <select
                                            value={prioridadSoporte}
                                            onChange={(e) => setPrioridadSoporte(e.target.value)}
                                            className="form-control"
                                            required
                                        >
                                            <option value="alta">Alta</option>
                                            <option value="media">Media</option>
                                            <option value="baja">Baja</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group col-md-6">
                                        <label className="text-muted small font-weight-semibold">Solución aplicada</label>
                                        <input
                                            placeholder="Resultado o acciones ejecutadas"
                                            value={solucion}
                                            onChange={(e) => setSolucion(e.target.value)}
                                            className="form-control"
                                        />
                                    </div>
                                    <div className="form-group col-md-6">
                                        <label className="text-muted small font-weight-semibold">Categoria de falla</label>
                                        <select
                                            value={categoriaFalla}
                                            onChange={(e) => setCategoriaFalla(normalizarTextoCategoria(e.target.value))}
                                            className="form-control"
                                        >
                                            <option value="">Seleccionar categoria...</option>
                                            {CATEGORIAS_FALLA.map((categoria) => (
                                                <option key={categoria} value={categoria}>
                                                    {categoria}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <SoporteAvisoTerreno tipo={tipo} estado={estado} fechaCierre={fechaCierre}
                                    sugerencia={sugerencia} onSugerencia={setSugerencia}
                                    enviar={enviarAvisoTerreno} onEnviar={setEnviarAvisoTerreno}
                                    disabled={guardandoSoporte} aviso={editarSoporte?.aviso_terreno} />
                                {subcategoriasDisponibles.length > 0 && (
                                    <div className="form-row">
                                        <div className="form-group col-md-6">
                                            <label className="text-muted small font-weight-semibold">Subcategoria</label>
                                            <select
                                                value={subcategoriaFalla}
                                                onChange={(e) => setSubcategoriaFalla(normalizarTextoCategoria(e.target.value))}
                                                className="form-control"
                                            >
                                                <option value="">Seleccionar subcategoria...</option>
                                                {subcategoriasDisponibles.map((subcategoria) => (
                                                    <option key={subcategoria} value={subcategoria}>
                                                        {subcategoria}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                )}

                                <div className="form-row">
                                    <div className="form-group col-md-6">
                                        <label className="text-muted small font-weight-semibold">Estado</label>
                                        <select
                                            value={estado}
                                            onChange={(e) => setEstado(e.target.value)}
                                            className="form-control"
                                        >
                                            <option value="pendiente">Pendiente</option>
                                            <option value="en_proceso">Alerta</option>
                                            <option value="resuelto">Resuelto</option>
                                        </select>
                                        <div className="estado-preview mt-2">
                                            {renderEstado(estado)}
                                        </div>
                                        <div className="form-row align-items-end mt-2">
                                            <div className="form-group col-md-8 mb-0">
                                                <label className="text-muted small font-weight-semibold">Case code</label>
                                                <input
                                                    placeholder="Ej: CS-2026-001"
                                                    value={caseCode}
                                                    onChange={(e) => setCaseCode(String(e.target.value || "").trim())}
                                                    className="form-control"
                                                />
                                            </div>
                                            <div className="form-group col-md-4 mb-0">
                                                <label className="text-muted small font-weight-semibold d-block">Permiso trabajo</label>
                                                <label className={`permiso-trabajo-toggle ${permisoTrabajo ? "activo" : "inactivo"}`}>
                                                    <input
                                                        type="checkbox"
                                                        checked={permisoTrabajo}
                                                        onChange={(e) => setPermisoTrabajo(e.target.checked)}
                                                    />
                                                    <span className="permiso-trabajo-toggle-icon">
                                                        <i className="fas fa-user-cog"></i>
                                                    </span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="form-group col-md-6">
                                        <label className="text-muted small font-weight-semibold d-flex justify-content-between align-items-center">
                                            <span>Fecha de cierre</span>
                                            <button
                                                type="button"
                                                className="btn btn-link btn-sm p-0 text-primary font-weight-semibold atajo-hoy"
                                                onClick={() => handleFechaCierreChange(new Date().toISOString().slice(0, 10))}
                                            >
                                                Hoy
                                            </button>
                                        </label>
                                        <input
                                            type="date"
                                            value={fechaCierre}
                                            onChange={(e) => handleFechaCierreChange(e.target.value)}
                                            className={`form-control ${errorFechaCierre ? "is-invalid" : ""}`}
                                            placeholder="Fecha de cierre"
                                        />
                                        {errorFechaCierre && (
                                            <small className="text-danger d-block mt-1">{errorFechaCierre}</small>
                                        )}
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group col-md-6 d-flex align-items-center soporte-switch">
                                        <input
                                            type="checkbox"
                                            className="mr-2"
                                            checked={cambioEquipo}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                setCambioEquipo(checked);
                                                if (!checked) {
                                                    setCantidadEquiposCambiados("");
                                                    setDetalleEquiposCambiadosLista([]);
                                                }
                                            }}
                                            id="cambio-equipo"
                                        />
                                        <label htmlFor="cambio-equipo" className="mb-0">¿Cambio de equipo?</label>
                                    </div>
                                    <div className="form-group col-md-6">
                                        <label className="text-muted small font-weight-semibold">Cantidad de equipos</label>
                                        <input
                                            type="number"
                                            min="1"
                                            placeholder="Ej: 2"
                                            value={cantidadEquiposCambiados}
                                            onChange={(e) => {
                                                const raw = e.target.value;
                                                setCantidadEquiposCambiados(raw);
                                                const cantidad = Number(raw || 0);
                                                setDetalleEquiposCambiadosLista((prev) =>
                                                    normalizarListaDetalles(cantidad, prev)
                                                );
                                            }}
                                            className="form-control"
                                            disabled={!cambioEquipo}
                                        />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label className="text-muted small font-weight-semibold">Detalle equipo reemplazado</label>
                                    {!cambioEquipo && (
                                        <input
                                            placeholder="Activa 'Cambio de equipo' para completar detalle"
                                            className="form-control"
                                            disabled
                                        />
                                    )}
                                    {cambioEquipo &&
                                        normalizarListaDetalles(
                                            Number(cantidadEquiposCambiados || 0),
                                            detalleEquiposCambiadosLista
                                        ).map((detalle, index) => (
                                            <div key={`detalle-equipo-${index}`} className="mb-2">
                                                <small className="text-muted d-block mb-1">Equipo {index + 1}</small>
                                                <input
                                                    placeholder={`Detalle equipo ${index + 1} (ej: BATERIA / ROUTER)`}
                                                    value={detalle}
                                                    onChange={(e) => {
                                                        const value = e.target.value.toUpperCase();
                                                        setDetalleEquiposCambiadosLista((prev) => {
                                                            const cantidad = Number(cantidadEquiposCambiados || 0);
                                                            const lista = normalizarListaDetalles(cantidad, prev);
                                                            lista[index] = value;
                                                            return lista;
                                                        });
                                                    }}
                                                    className="form-control text-uppercase"
                                                />
                                            </div>
                                        ))}
                                    {cambioEquipo && Number(cantidadEquiposCambiados || 0) > 0 && (
                                        <small className="text-muted">
                                            Completa un detalle por cada equipo reemplazado.
                                        </small>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer">
	                                <button className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={guardandoSoporte}>
	                                    Cerrar
	                                </button>
	                                <button className="btn btn-primary" onClick={handleGuardarSoporte} disabled={guardandoSoporte}>
	                                    {guardandoSoporte ? (
	                                        <>
	                                            <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
	                                            Guardando...
	                                        </>
	                                    ) : (
	                                        "Guardar"
	                                    )}
	                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {diagramaConsulta.abierto && (
                <div className="modal show soporte-diagram-modal" style={{ display: "block" }} role="dialog" aria-modal="true">
                    <div className="modal-dialog modal-xl modal-dialog-centered">
                        <div className="modal-content soporte-diagram-content shadow-lg border-0">
                            <div className="modal-header soporte-diagram-header">
                                <div>
                                    <small>Diagrama operativo</small>
                                    <h5>{diagramaConsulta.plantilla?.nombre || diagramaConsulta.soporte?.centro?.nombre || "Centro"}</h5>
                                    <span>
                                        {diagramaConsulta.soporte?.centro?.cliente || "Cliente"} · {diagramaConsulta.soporte?.centro?.nombre || "Centro"}
                                    </span>
                                </div>
                                <button className="close" onClick={() => setDiagramaConsulta((actual) => ({ ...actual, abierto: false }))} aria-label="Cerrar">
                                    &times;
                                </button>
                            </div>
                            <div className="modal-body soporte-diagram-body">
                                {diagramaConsulta.cargando ? (
                                    <div className="soporte-diagram-status">
                                        <i className="fas fa-spinner fa-spin"></i>
                                        <strong>Cargando diagrama...</strong>
                                    </div>
                                ) : diagramaConsulta.error ? (
                                    <div className="soporte-diagram-status error">
                                        <i className="fas fa-project-diagram"></i>
                                        <strong>{diagramaConsulta.error}</strong>
                                        <span>Asigna una plantilla desde la pagina Configuraciones.</span>
                                    </div>
                                ) : (
                                    <>
                                        <div className="soporte-diagram-meta">
                                            <span><i className="fas fa-eye"></i> Solo lectura</span>
                                            <span className={`scope-${diagramaConsulta.origen || "general"}`}>
                                                Plantilla de {diagramaConsulta.origen === "centro" ? "centro" : diagramaConsulta.origen === "cliente" ? "cliente" : "uso general"}
                                            </span>
                                        </div>
                                        <DiagramaLogicoViewer plantilla={diagramaConsulta.plantilla} />
                                    </>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setDiagramaConsulta((actual) => ({ ...actual, abierto: false }))}>Cerrar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {ismaelSeleccionado && (
                <div className="modal show" style={{ display: "block" }}>
                    <div className={`modal-dialog ${ismaelSeleccionado.tipo_fuente === "dispositivo" ? "modal-xl" : "modal-lg"} modal-dialog-scrollable`}>
                        <div className="modal-content soporte-modal shadow-lg border-0 ismael-modal">
                            <div className="modal-header ismael-modal-header">
                                <h5>
                                    <i className={`fas ${ismaelSeleccionado.tipo_fuente === "dispositivo" ? "fa-satellite-dish" : "fa-envelope-open-text"} mr-2`}></i>
                                    {ismaelSeleccionado.tipo_fuente === "dispositivo" ? "Detalle de falla de dispositivo" : "Detalle caso ismael"}
                                </h5>
                                <button className="close" onClick={cerrarDetalleCaso}>
                                    &times;
                                </button>
                            </div>
                            <div className="modal-body soporte-form-body ismael-modal-body">
                                {ismaelSeleccionado.tipo_fuente === "dispositivo" ? (
                                <>
                                <div className="device-detail-summary">
                                    <div className="device-detail-item">
                                        <i className="fas fa-building"></i>
                                        <span><small>Cliente</small><strong>{ismaelSeleccionado.cliente || "-"}</strong></span>
                                    </div>
                                    <div className="device-detail-item">
                                        <i className="fas fa-map-marker-alt"></i>
                                        <span><small>Centro</small><strong>{ismaelSeleccionado.centro || "-"}</strong></span>
                                    </div>
                                    <div className="device-detail-item">
                                        <i className="fas fa-microchip"></i>
                                        <span><small>Dispositivo</small><strong>{ismaelSeleccionado.device_name || ismaelSeleccionado.entity_type || "-"}</strong></span>
                                    </div>
                                    <div className="device-detail-item">
                                        <i className="fas fa-network-wired"></i>
                                        <span><small>IP objetivo</small><strong>{ismaelSeleccionado.target_ip || "-"}</strong></span>
                                    </div>
                                    <div className="device-detail-item secondary">
                                        <i className="fas fa-wave-square"></i>
                                        <span><small>Control</small><strong>{ismaelSeleccionado.check_type || "-"}</strong></span>
                                    </div>
                                    <div className="device-detail-item secondary">
                                        <i className="fas fa-signal"></i>
                                        <span>
                                            <small>Estado</small>
                                            <strong className={`device-detail-status ${ismaelSeleccionado.estado === "recuperado" ? "recovered" : "active"}`}>
                                                {ismaelSeleccionado.estado === "recuperado" ? "Recuperado" : "Falla activa"}
                                            </strong>
                                        </span>
                                    </div>
                                    <div className="device-detail-item secondary">
                                        <i className="far fa-clock"></i>
                                        <span><small>Fuera de linea</small><strong>{formatearFechaHora(ismaelSeleccionado.offline_since)}</strong></span>
                                    </div>
                                    <div className="device-detail-item secondary">
                                        <i className="fas fa-check-circle"></i>
                                        <span><small>Recuperado</small><strong>{formatearFechaHora(ismaelSeleccionado.recovered_at)}</strong></span>
                                    </div>
                                </div>
                                <section className="device-topology-section">
                                    <div className="device-topology-heading">
                                        <div>
                                            <small>Topologia asociada</small>
                                            <h6>Ubicacion de la falla activa</h6>
                                        </div>
                                        {!topologiaFalla.cargando && topologiaFalla.plantilla && (
                                            <span className="device-topology-scope">
                                                Plantilla de {topologiaFalla.origen === "centro" ? "centro" : topologiaFalla.origen === "cliente" ? "cliente" : "uso general"}
                                            </span>
                                        )}
                                    </div>
                                    {topologiaFalla.cargando ? (
                                        <div className="device-topology-state"><i className="fas fa-spinner fa-spin" /><strong>Cargando topologia...</strong></div>
                                    ) : topologiaFalla.error ? (
                                        <div className="device-topology-state warning"><i className="fas fa-exclamation-triangle" /><strong>{topologiaFalla.error}</strong></div>
                                    ) : topologiaFalla.plantilla ? (
                                        <>
                                            <div className="device-fault-banner">
                                                <span><i className="fas fa-circle-exclamation" /> Falla activa</span>
                                                <strong>{topologiaFalla.falla?.equipo_nombre || ismaelSeleccionado.device_name || "Dispositivo"}</strong>
                                                <small>{ismaelSeleccionado.target_ip || "Sin IP informada"}</small>
                                            </div>
                                            <div className="device-topology-view">
                                                <div className="device-topology-view-title">
                                                    <span><i className="fas fa-ship" /> Vista general del ponton</span>
                                                    <small>Ubicacion fisica</small>
                                                </div>
                                                <VistaGeneralViewer plantilla={topologiaFalla.plantilla} fallaActiva={topologiaFalla.falla} />
                                            </div>
                                            <div className="device-topology-view">
                                                <div className="device-topology-view-title">
                                                    <span><i className="fas fa-project-diagram" /> Diagrama logico</span>
                                                    <small>Conexion entre equipos</small>
                                                </div>
                                                <DiagramaLogicoViewer plantilla={topologiaFalla.plantilla} fallaActiva={topologiaFalla.falla} />
                                            </div>
                                        </>
                                    ) : null}
                                </section>
                                </>
                                ) : (
                                <div className="row">
                                    <div className="col-md-6 mb-2"><div className="ismael-field"><small>Case code</small><strong>{ismaelSeleccionado.case_code || "-"}</strong></div></div>
                                    <div className="col-md-6 mb-2"><div className="ismael-field"><small>Centro</small><strong>{ismaelSeleccionado.centro || "-"}</strong></div></div>
                                    <div className="col-md-6 mb-2"><div className="ismael-field"><small>Hora llegada</small><strong>{formatearFechaHora(ismaelSeleccionado.hora_llegada)}</strong></div></div>
                                    <div className="col-md-12 mb-2">
                                        <div className="ismael-problem-card">
                                            <div className="ismael-problem-title">
                                                <i className="fas fa-triangle-exclamation mr-2"></i>
                                                Resumen del problema
                                            </div>
                                            <div className="ismael-problem-body">
                                                {ismaelSeleccionado.falla_especifica || "Sin falla especifica registrada."}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-12 mb-2">
                                        <div className="ismael-field ismael-field-mail">
                                            <div className="ismael-mail-heading">
                                                <small>Correo principal</small>
                                                <button
                                                    type="button"
                                                    className="ismael-mail-toggle"
                                                    title={mostrarCorreoIsmael ? "Ocultar correo" : "Ver correo"}
                                                    aria-label={mostrarCorreoIsmael ? "Ocultar correo principal" : "Ver correo principal"}
                                                    onClick={() => setMostrarCorreoIsmael((visible) => !visible)}
                                                >
                                                    <i className={`fas ${mostrarCorreoIsmael ? "fa-eye-slash" : "fa-eye"}`}></i>
                                                </button>
                                            </div>
                                            {mostrarCorreoIsmael && (
                                                <strong className="ismael-mail-content">{ismaelSeleccionado.correo || "Sin contenido de correo."}</strong>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-12 mb-2"><div className="ismael-field"><small>Respuesta final</small><strong>{ismaelSeleccionado.respuesta_final || "-"}</strong></div></div>
                                </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-success" onClick={() => abrirAtajoSoporteDesdeIsmael(ismaelSeleccionado)}>
                                    <i className="fas fa-plus mr-1"></i>
                                    Crear soporte
                                </button>
                                <button className="btn btn-secondary" onClick={cerrarDetalleCaso}>
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Soporte;

